import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProfiles } from '../../context/ProfileContext';
import { AVATARS } from '../../context/ProfileContext';
import LoginModal from '../ui/LoginModal';

const SearchIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>);
const ChevronDown = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>);
const UserIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>);
const ListIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>);
const CreditCard = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="22" height="16" x="1" y="4" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>);
const TvIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="15" rx="2"/><polyline points="17 2 12 7 7 2"/></svg>);
const LogOut = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>);

const HamburgerIcon = ({ open }) => open ? (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
) : (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
);

const SwitchIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 2l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>);

export default function Navbar({ onSearchOpen }) {
  const [scrolled,    setScrolled]    = useState(false);
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [mobileNav,   setMobileNav]   = useState(false);
  const [loginOpen,   setLoginOpen]   = useState(false);
  const { user, userData, signOut, isAuthenticated, showSubscribeButton } = useAuth();
  const { activeProfile } = useProfiles();
  const navigate  = useNavigate();
  const location  = useLocation();
  const avatarRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      // Close dropdowns when user scrolls
      if (menuOpen)  setMenuOpen(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [menuOpen]);

  useEffect(() => { setMobileNav(false); }, [location.pathname]);

  useEffect(() => {
    const close = (e) => { if (avatarRef.current && !avatarRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <Link to="/" className="nav-logo">
          <img src="/assets/brand/logo.png" alt="Shortflix" style={{ height: '28px', width: 'auto', maxWidth: '130px' }} />
        </Link>

        <ul className="nav-links">
          <li><Link to="/"       className={isActive('/')       ? 'active' : ''}>Home</Link></li>
          <li><Link to="/movies" className={isActive('/movies') ? 'active' : ''}>Movies</Link></li>
          <li><Link to="/series" className={isActive('/series') ? 'active' : ''}>Series</Link></li>
          {isAuthenticated && <li><Link to="/mylist" className={isActive('/mylist') ? 'active' : ''}>My List</Link></li>}
          <li>
            <a href="https://blog.shortflixindia.com" target="_blank" rel="noopener noreferrer" className="nav-link-highlight">
              Blog
            </a>
          </li>
        </ul>

        <div className="nav-right">
          <a href="https://gateway.shortflixindia.com" target="_blank" rel="noopener noreferrer" className="btn-submit-film">
            + Submit Film
          </a>
          <button className="btn-icon" onClick={onSearchOpen} title="Search"><SearchIcon /></button>
          {isAuthenticated && (
            <div style={{ position:'relative' }}>
                </div>
          )}

          {/* Subscribe button — only for logged-in users who aren't premium yet.
              Logged-out users see "My Shortflix" only (below); showing Subscribe
              too was redundant since both just opened the same login prompt. */}
          {isAuthenticated && showSubscribeButton && (
            <button className="btn-subscribe" onClick={() => navigate('/subscription')}>
              Subscribe
            </button>
          )}

          {/* Hamburger — mobile only */}
          <button
            className="hamburger-btn"
            onClick={() => setMobileNav(o => !o)}
            aria-label="Menu">
            <HamburgerIcon open={mobileNav} />
          </button>

          {/* Avatar — desktop only */}
          {isAuthenticated ? (
            <div className="avatar-wrap" ref={avatarRef}>
              <button className="avatar-btn avatar-trigger" onClick={() => setMenuOpen(o => !o)}>
                {/* Priority: real Google/Firebase photo URL first, then gradient fallback */}
                {user?.photoURL ? (
                  <>
                    <img src={user.photoURL} alt="profile" className="avatar-img"
                      referrerPolicy="no-referrer"
                      onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                    />
                    <div className="avatar-placeholder" style={{ display:'none' }}>
                      {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
                    </div>
                  </>
                ) : (
                  <div
                    className="avatar-img nav-profile-dot"
                    style={{
                      background: activeProfile?.isKids
                        ? 'linear-gradient(135deg,#7C3AED,#EC4899)'
                        : (AVATARS[activeProfile?.avatarId ?? 0]?.bg ?? AVATARS[0].bg),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 700, fontSize: 13,
                    }}
                  >
                    {(activeProfile?.name || user?.displayName || user?.email || 'U')[0].toUpperCase()}
                  </div>
                )}
                <ChevronDown />
              </button>
              {menuOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-user">
                    <div className="dropdown-name">{activeProfile?.name || user?.displayName || user?.email}</div>
                    {userData?.membershipdisplayname && <div className="dropdown-plan">{userData.membershipdisplayname}</div>}
                  </div>
                  <div className="dropdown-divider" />
                  <button onClick={() => { navigate('/profile'); setMenuOpen(false); }}><UserIcon /> My Profile</button>
                  <button onClick={() => { navigate('/mylist'); setMenuOpen(false); }}><ListIcon /> My List</button>
                  <button onClick={() => { navigate('/profile?tv=1'); setMenuOpen(false); }}><TvIcon /> Activate TV</button>
                  <button onClick={() => { navigate('/subscription'); setMenuOpen(false); }}><CreditCard /> Subscription</button>
                  <div className="dropdown-divider" />
                  <button className="sign-out-btn" onClick={async () => {
                    setMenuOpen(false);
                    await signOut();
                    setLoginOpen(true);
                  }}><LogOut /> Sign Out</button>
                </div>
              )}
            </div>
          ) : (
            /* "My Shortflix" button when not logged in */
            <button className="btn-my-shortflix" onClick={() => setLoginOpen(true)}>
              My Shortflix
            </button>
          )}
        </div>
      </nav>

      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />

      {/* ── Mobile nav overlay ── */}
      {mobileNav && (
        <div className="mobile-nav-overlay" onClick={() => setMobileNav(false)}>
          <div className="mobile-nav-panel" onClick={e => e.stopPropagation()}>
            <div className="mobile-nav-header">
              <img src="/assets/brand/logo.png" alt="Shortflix" style={{ height: 24 }} />
              <button className="mobile-nav-close" onClick={() => setMobileNav(false)}>✕</button>
            </div>

            {/* User info if logged in */}
            {isAuthenticated && (
              <div className="mobile-nav-user" style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{
                    width:40, height:40, borderRadius:'50%', flexShrink:0,
                    background: activeProfile
                      ? (activeProfile.isKids ? 'linear-gradient(135deg,#7C3AED,#EC4899)' : (AVATARS[activeProfile.avatarId ?? 0]?.bg ?? AVATARS[0].bg))
                      : 'var(--accent)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    color:'#fff', fontWeight:700, fontSize:15,
                  }}>
                    {(activeProfile?.name || user?.displayName || 'U')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ color:'#fff', fontWeight:600, fontSize:14 }}>{activeProfile?.name || user?.displayName || user?.email}</div>
                    {userData?.membershipdisplayname && <div style={{ color:'var(--accent)', fontSize:12, fontWeight:600 }}>{userData.membershipdisplayname}</div>}
                  </div>
                </div>
              </div>
            )}

            <nav className="mobile-nav-links">
              <Link to="/"        className={location.pathname==='/'       ? 'active':''} onClick={() => setMobileNav(false)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> Home
              </Link>
              <Link to="/movies"  className={location.pathname==='/movies' ? 'active':''} onClick={() => setMobileNav(false)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg> Movies
              </Link>
              <Link to="/series"  className={location.pathname==='/series' ? 'active':''} onClick={() => setMobileNav(false)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="15" rx="2"/><polyline points="17 2 12 7 7 2"/></svg> Series
              </Link>
              {isAuthenticated && (
                <Link to="/mylist" className={location.pathname==='/mylist' ? 'active':''} onClick={() => setMobileNav(false)}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> My List
                </Link>
              )}

              <div className="mobile-nav-divider" />

              {isAuthenticated ? (
                <>
                  <button onClick={() => { navigate('/profile'); setMobileNav(false); }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> My Profile
                  </button>
                  {showSubscribeButton && (
                    <Link to="/subscription" onClick={() => setMobileNav(false)}>
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> Subscribe
                    </Link>
                  )}
                  <div className="mobile-nav-divider" />
                  <button onClick={async () => { setMobileNav(false); await signOut(); setLoginOpen(true); }} style={{ color:'#f87171' }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg> Sign Out
                  </button>
                </>
              ) : (
                <>
                  {/* Logged-out mobile: just Sign In — a separate Subscribe link
                      here was redundant, it opened the same login prompt. */}
                  <button onClick={() => { setMobileNav(false); setLoginOpen(true); }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> Sign In
                  </button>
                </>
              )}

              <div className="mobile-nav-divider" />
              <a href="https://gateway.shortflixindia.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileNav(false)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Submit Film
              </a>
              <a href="https://blog.shortflixindia.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileNav(false)} className="nav-link-highlight">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg> Blog
              </a>
              <Link to="/about" onClick={() => setMobileNav(false)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> About
              </Link>
              <Link to="/faq" onClick={() => setMobileNav(false)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> FAQ
              </Link>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
